/**
 * King Living override for useMediaCarousel hooks.
 * Defaults to `full` variant so slides (e.g. ShopTheLook) include nested fragment data.
 */

import {print} from 'graphql';

import {useQuery} from '@tanstack/react-query';

import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {
  getBaseMediaCarouselById,
  getBaseMediaCarousels,
  getFullMediaCarouselById,
  getFullMediaCarousels,
  getMediaCarouselById,
  getMediaCarousels,
} from '@amblique/contentful/app/contentful/queries';

export function useMediaCarouselById(id, options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query =
    variant === 'base' ?
      getBaseMediaCarouselById :
      variant === 'full' ?
        getFullMediaCarouselById :
        getMediaCarouselById;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'mediaCarousel',
      'id',
      id,
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          id,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.mediaCarousel || null;
    },

    enabled: !!contentfulService && !!id,

    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,

    ...queryOptions,
  });
}

export function useMediaCarousels(options = {}) {
  const contentfulService = useService(ContentfulService);

  const {
    variant = 'full',
    variables = {},
    queryOptions = {},
    requestOptions = {},
  } = options;

  const query =
    variant === 'base' ?
      getBaseMediaCarousels :
      variant === 'full' ?
        getFullMediaCarousels :
        getMediaCarousels;

  return useQuery({
    queryKey: [
      contentfulService?.key,
      'mediaCarousel',
      'collection',
      variant,
      contentfulService?.locale,
      JSON.stringify(variables)
    ],

    queryFn: async () => {
      if (!contentfulService) {
        throw new Error('ContentfulService not available');
      }

      const data = await contentfulService.query(
        print(query),
        {
          limit: 10,
          skip: 0,
          locale: contentfulService?.locale,
          preview: false,
          ...variables,
        },
        requestOptions
      );

      return data.mediaCarouselCollection || {total: 0, skip: 0, limit: 0, items: []};
    },

    enabled: !!contentfulService,

    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,

    retry: (failureCount, error) => {
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }

      return failureCount < 3;
    },

    ...queryOptions,
  });
}
